import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Users, ShoppingBag, FileText, TrendingUp, type LucideIcon } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";
import { useCountUp } from "@/hooks/useCountUp";

interface Stats {
  totalListings: number;
  completedSales: number;
  activeUsers: number;
  totalUsers: number;
}

interface StatItem {
  icon: LucideIcon;
  label: string;
  value: number;
  suffix?: string;
  gradient: string;
}

const StatCard = ({ item, index, start }: { item: StatItem; index: number; start: boolean }) => {
  const Icon = item.icon;
  const display = useCountUp(item.value, 1600, start);
  return (
    <div
      style={{ transitionDelay: start ? `${index * 100}ms` : "0ms" }}
      className={`group flex flex-col items-center text-center transition-all duration-700 ease-out ${
        start ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      }`}
    >
      <div className="relative mb-3 inline-flex">
        <div
          className={`absolute inset-0 -z-10 rounded-2xl bg-gradient-to-br ${item.gradient} opacity-30 blur-md transition-opacity duration-300 group-hover:opacity-60`}
          aria-hidden
        />
        <div
          className={`flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${item.gradient} text-primary-foreground shadow-lg shadow-primary/20 transition-transform duration-300 group-hover:scale-110`}
        >
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <span className="font-display text-3xl font-extrabold tabular-nums md:text-4xl">
        {display.toLocaleString("es-ES")}
        {item.suffix}
      </span>
      <span className="mt-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {item.label}
      </span>
    </div>
  );
};

const PortalStats = () => {
  const [stats, setStats] = useState<Stats>({ totalListings: 0, completedSales: 0, activeUsers: 0, totalUsers: 0 });
  const { ref, visible } = useReveal<HTMLDivElement>();

  useEffect(() => {
    if (!visible) return;
    const load = async () => {
      const [listingsRes, soldRes, profilesRes] = await Promise.all([
        supabase.from("listings").select("id", { count: "exact", head: true }).eq("status", "active"),
        supabase.from("listings").select("id", { count: "exact", head: true }).eq("status", "sold"),
        supabase.from("profiles").select("id", { count: "exact", head: true }),
      ]);
      setStats({
        totalListings: listingsRes.count || 0,
        completedSales: soldRes.count || 0,
        activeUsers: profilesRes.count || 0,
        totalUsers: profilesRes.count || 0,
      });
    };
    load();
  }, [visible]);

  const items: StatItem[] = [
    { icon: FileText, label: "Anuncios activos", value: stats.totalListings, gradient: "from-primary to-accent" },
    { icon: ShoppingBag, label: "Ventas completadas", value: stats.completedSales, gradient: "from-secondary to-primary" },
    { icon: Users, label: "Usuarios registrados", value: stats.totalUsers, gradient: "from-accent to-secondary" },
    { icon: TrendingUp, label: "Comunidad activa", value: stats.activeUsers, suffix: "+", gradient: "from-primary to-secondary" },
  ];

  return (
    <section className="relative overflow-hidden border-b border-border py-12">
      <div className="pointer-events-none absolute -top-20 left-1/2 h-64 w-[40rem] -translate-x-1/2 rounded-full bg-primary/10 blur-3xl" aria-hidden />
      <div ref={ref} className="container relative">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
          {items.map((item, idx) => (
            <StatCard key={item.label} item={item} index={idx} start={visible} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default PortalStats;
